import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes, UITypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

export const notificationApi = createApi({
  reducerPath: "notifications",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["NotificationsList", "InAppTopNotifications", "InAppUnreadCount", "NotificationSettings"],
  endpoints: builder => ({
    getNotifications: builder.query<ApiTypes.Res.Notifications, UITypes.NotificationParams>({
      query: arg => ({
        url: `/notification/notifications`,
        params: prepareQueryParams(arg),
        headers: { Accept: "application/json" },
      }),
      providesTags: ["NotificationsList"],
    }),
    getInAppUnreadCount: builder.query<{ data: number }, void>({
      query: () => ({
        url: `/notification/inapp-unread-count`,
        headers: { Accept: "application/json" },
      }),
      keepUnusedDataFor: 0,
      providesTags: ["InAppUnreadCount"],
    }),
    getInAppTopNotifications: builder.query<ApiTypes.Res.InAppTopNotifications, { max_id?: number } | void>({
      query: arg => ({
        url: `/notification/inapp-top`,
        params: arg && "max_id" in arg && arg.max_id ? { max_id: arg.max_id } : undefined,
        headers: { Accept: "application/json" },
      }),
      keepUnusedDataFor: 0,
      providesTags: ["InAppTopNotifications"],
    }),
    getEventTypes: builder.query<ApiTypes.Res.EventTypes, void>({
      query: () => ({
        url: `/notification/event-types`,
        headers: { Accept: "application/json" },
      }),
    }),
    getNotificationSettings: builder.query<ApiTypes.Res.NotificationSettings, void>({
      query: () => ({
        url: `/notification/settings`,
        headers: { Accept: "application/json" },
      }),
      keepUnusedDataFor: 0,
      providesTags: ["NotificationSettings"],
    }),
    getUserEventTypes: builder.query<ApiTypes.Res.UserEventTypes, void>({
      query: () => ({
        url: `/notification/user-event-types`,
        headers: { Accept: "application/json" },
      }),
    }),
    updateNotificationSettings: builder.mutation<void, ApiTypes.Req.NotificationSettings>({
      query: body => ({
        url: `/notification/settings`,
        method: "POST",
        body,
        headers: { Accept: "application/json" },
      }),
    }),
    markAsRead: builder.mutation<void, number>({
      query: id => ({
        url: `/notification/mark-as-read`,
        method: "POST",
        params: { id },
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["NotificationsList", "InAppUnreadCount", "InAppTopNotifications"],
    }),
    markAsUnread: builder.mutation<void, number>({
      query: id => ({
        url: `/notification/mark-as-unread`,
        method: "POST",
        params: { id },
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["NotificationsList", "InAppUnreadCount", "InAppTopNotifications"],
    }),
    markAllAsRead: builder.mutation<void, void>({
      query: () => ({
        url: `/notification/mark-all-as-read`,
        method: "POST",
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["NotificationsList", "InAppTopNotifications", "InAppUnreadCount"],
    }),
  }),
})

export const {
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetInAppUnreadCountQuery,
  useGetInAppTopNotificationsQuery,
  useLazyGetInAppTopNotificationsQuery,
  useGetEventTypesQuery,
  useGetUserEventTypesQuery,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useMarkAsReadMutation,
  useMarkAsUnreadMutation,
  useMarkAllAsReadMutation,
} = notificationApi
